import React, { useEffect, useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import { HoroscopesEdge } from '@moment/components/Horoscopes/HoroscopesEdge/HoroscopesEdge';
import { HoroscopeCard } from '@moment/components/Horoscopes/HoroscopesCard/HoroscopesCard';
import { HoroscopesGrid } from '@moment/components/Horoscopes/HoroscopesGrid/HoroscopesGrid';
import {
  HoroscopeIconCategory,
  HoroscopeIconType,
} from '@moment/components/Horoscopes/HoroscopesIcon/HoroscopesIcon-Constants';
import { genFetchHoroscopesQuery } from '@moment/net/classic-fotoscapes/fetch-horoscopes';
import ActivityIndicator from '@moment/components/ActivityIndicator/ActivityIndicator';
import localstorage from '@/pages/Root/services/storage/localstorage';
import { isHoroscopeCacheValid, recordSignClick, } from '@moment/components/HoroscopesV2/HoroscopesV2Util';

import './HoroscopesLayout.scss';

interface HoroscopesLayoutProps {
  queryFn?: (params: { sign?: string; lang?: string }) => Promise<any>;
  showEdge?: boolean;
  showCards?: boolean;
  showGrid?: boolean;
  handleWidgetClick?: (selectedSign: string) => void;
}

const HOROSCOPES_STORAGE_KEY = 'horoscopes-data';

export const HoroscopesLayout: React.FC<HoroscopesLayoutProps> = ({
  queryFn,
  showEdge = true,
  showCards = true,
  showGrid = true,
  handleWidgetClick,
}) => {
  const { sign } = useParams<{ sign: string }>();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const lang = i18n.language;
  const navigate = useNavigate();
  const [cachedData, setCachedData] = useState<any>(null);
  const [isLoadingFromCache, setIsLoadingFromCache] = useState(true);

  // Helper to determine horoscope sign from a given date
  const getSignByDate = (date: Date): HoroscopeIconType => {
    const month = date.getMonth() + 1; // 1-12
    const day = date.getDate();

    if ((month === 3 && day >= 21) || (month === 4 && day <= 19))
      return HoroscopeIconType.aries;
    if ((month === 4 && day >= 20) || (month === 5 && day <= 20))
      return HoroscopeIconType.taurus;
    if ((month === 5 && day >= 21) || (month === 6 && day <= 21))
      return HoroscopeIconType.gemini;
    if ((month === 6 && day >= 22) || (month === 7 && day <= 22))
      return HoroscopeIconType.cancer;
    if ((month === 7 && day >= 23) || (month === 8 && day <= 22))
      return HoroscopeIconType.leo;
    if ((month === 8 && day >= 23) || (month === 9 && day <= 22))
      return HoroscopeIconType.virgo;
    if ((month === 9 && day >= 23) || (month === 10 && day <= 23))
      return HoroscopeIconType.libra;
    if ((month === 10 && day >= 24) || (month === 11 && day <= 22))
      return HoroscopeIconType.scorpio;
    if ((month === 11 && day >= 23) || (month === 12 && day <= 21))
      return HoroscopeIconType.sagittarius;
    if ((month === 12 && day >= 22) || (month === 1 && day <= 19))
      return HoroscopeIconType.capricorn;
    if ((month === 1 && day >= 20) || (month === 2 && day <= 18))
      return HoroscopeIconType.aquarius;
    if ((month === 2 && day >= 19) || (month === 3 && day <= 20))
      return HoroscopeIconType.pisces;

    return HoroscopeIconType.aries; // Fallback
  };

  // Convert sign parameter to HoroscopeIconType, or calculate from current date
  const signType = useMemo(() => {
    if (sign) {
      const normalizedSign = sign.toLowerCase();
      const foundSign = Object.values(HoroscopeIconType).find(
        (type) => type === normalizedSign
      );
      if (foundSign) return foundSign;
    }
    // If no sign in URL or sign is invalid, calculate from today's date
    return getSignByDate(new Date());
  }, [sign]);

  // Check localStorage for cached data (only use if content period has not ended)
  useEffect(() => {
    const storageKey = `${HOROSCOPES_STORAGE_KEY}-${lang}`;
    const cached = localstorage?.get(storageKey);

    if (cached && isHoroscopeCacheValid(cached)) {
      setCachedData(cached);
    }
    setIsLoadingFromCache(false);
  }, [lang]);

  // Generate queries for all horoscope categories
  const queriesToRun = useMemo(() => {
    // If we have cached data, return empty array to skip queries
    if (cachedData) {
      return [];
    }
    // Use custom queryFn if provided, otherwise use default
    if (queryFn) {
      // Create queries using custom queryFn
      return [
        {
          queryKey: ['horoscopes', 'general', lang],
          queryFn: () => queryFn({ lang }),
        },
        {
          queryKey: ['horoscopes', 'love', lang],
          queryFn: () => queryFn({ lang }),
        },
        {
          queryKey: ['horoscopes', 'money', lang],
          queryFn: () => queryFn({ lang }),
        },
        {
          queryKey: ['horoscopes', 'health', lang],
          queryFn: () => queryFn({ lang }),
        },
      ];
    }
    return genFetchHoroscopesQuery({ lang });
  }, [lang, cachedData, queryFn]);

  // Execute all queries in parallel (only if no cached data)
  const queryResults = useQueries({
    queries: queriesToRun,
  });

  // Store data in localStorage when queries complete
  useEffect(() => {
    if (!cachedData && queryResults.length > 0) {
      const allLoaded = queryResults.every(
        (result) => !result.isLoading && result.data
      );
      const hasError = queryResults.some((result) => result.error);

      if (allLoaded && !hasError) {
        const dataToCache = queryResults.map((result) => result.data);
        const storageKey = `${HOROSCOPES_STORAGE_KEY}-${lang}`;
        localstorage?.set(storageKey, dataToCache);
        setCachedData(dataToCache);
      }
    }
  }, [queryResults, cachedData, lang]);

  // Use cached data if available, otherwise use query results
  const [generalData, loveData, moneyData, healthData] = useMemo(() => {
    if (cachedData) {
      return cachedData;
    }
    return queryResults.map((result) => result.data);
  }, [cachedData, queryResults]);

  const isLoading = useMemo(() => {
    if (isLoadingFromCache) return true;
    if (cachedData) return false;
    return queryResults.some((result) => result.isLoading);
  }, [isLoadingFromCache, cachedData, queryResults]);

  const hasError = useMemo(() => {
    if (cachedData) return false;
    return queryResults.some((result) => result.error);
  }, [cachedData, queryResults]);

  // Get the sign display name
  const getSignDisplayName = (signType: HoroscopeIconType): string => {
    return signType.charAt(0).toUpperCase() + signType.slice(1);
  };

  // Extract horoscope text from API response
  const extractHoroscopeText = (data: any) => {
    const translatedSign = t(signType, signType)?.toLowerCase();
    if (!data || !data.items || data.items.length === 0) {
      return t('horoscope.noData', 'No horoscope data available');
    }

    for (const fetchedItem of data.items) {
      if (translatedSign === fetchedItem?.horoscope?.sign?.toLowerCase()) {
        // API response will fall back to English if language does not exist, so need to check it as well
        return (
          fetchedItem.horoscope.horoscopetext?.[lang] ||
          fetchedItem.horoscope.horoscopetext?.['en'] ||
          t('horoscope.noText', 'No horoscope text available')
        );
      }
    }
    // If no matching sign was found after the loop
    return t('horoscope.noText', 'No horoscope text available');
  };

  // Extract date from API response
  const extractDate = (data: any): string => {
    if (!data || !data.items || data.items.length === 0) {
      return new Date().toLocaleDateString(lang);
    }

    const firstItem = data.items[0];
    if (firstItem.horoscope.end) {
      return new Date(firstItem.horoscope.end).toLocaleDateString(lang);
    }
    return new Date().toLocaleDateString(lang);
  };

  if (isLoading) {
    return (
      <div className="horoscopes-layout">
        <div className="horoscopes-layout__loading">
          <ActivityIndicator />
          <p>{t('horoscope.loading', 'Loading horoscope...')}</p>
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="horoscopes-layout">
        <div className="horoscopes-layout__error">
          <p>{t('horoscope.error', 'Error loading horoscope data')}</p>
        </div>
      </div>
    );
  }

  const signDisplayName = getSignDisplayName(signType);

  return (
    <div className="horoscopes-layout">
      <div className="horoscopes-layout__container">
        {/* HoroscopesEdge with general data */}
        {showEdge && (
          <div className="horoscopes-layout__edge">
            <HoroscopesEdge
              icon={signType}
              sign={signDisplayName}
              text={extractHoroscopeText(generalData)}
            />
          </div>
        )}

        {/* HoroscopesCards for love, money, and health */}
        {showCards && (
          <div className="horoscopes-layout__cards">
            <div className="horoscopes-layout__card">
              <HoroscopeCard
                icon={HoroscopeIconCategory.love}
                category={t('horoscope.love', 'Love')}
                sign={signDisplayName}
                date={extractDate(loveData)}
                text={extractHoroscopeText(loveData)}
              />
            </div>

            <div className="horoscopes-layout__card">
              <HoroscopeCard
                icon={HoroscopeIconCategory.money}
                category={t('horoscope.money', 'Money')}
                sign={signDisplayName}
                date={extractDate(moneyData)}
                text={extractHoroscopeText(moneyData)}
              />
            </div>

            <div className="horoscopes-layout__card">
              <HoroscopeCard
                icon={HoroscopeIconCategory.health}
                category={t('horoscope.health', 'Health')}
                sign={signDisplayName}
                date={extractDate(healthData)}
                text={extractHoroscopeText(healthData)}
              />
            </div>
          </div>
        )}

        {/* HoroscopesGrid */}
        {showGrid && (
          <div className="horoscopes-layout__grid">
            <HoroscopesGrid
              onSignSelect={(selectedSign) => {
                recordSignClick(selectedSign);
                navigate({
                  pathname: `/horoscope/${selectedSign}`,
                  search:
                    searchParams.size > 0 ? `?${searchParams.toString()}` : ``,
                });
                if (handleWidgetClick) {
                  handleWidgetClick(selectedSign?.toString());
                }
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
